/** Debug snapshot of PDM merge output. See docs/upholstery/pdm/ROW_CONFIGURATION_PDM_DATO_MAPPING.md §6.1 */
import { create } from "zustand"
import { devtools } from "zustand/middleware"
import type { OptionValue, FactoryMatch, Step } from "@views/Upholstery/types"
import type { PdmAssetRecord } from "@views/UpholsteryV2/types"

export interface PdmExtractedData {
  totalProducts: number
  matchedRows: number
  vehicleContext: {
    make: string
    model: string
    year: string
  } | null
  rawPdmProducts: Record<string, unknown>[] | null
  body: {
    trim: OptionValue[]
    bodyType: OptionValue[]
    driverSeatType: OptionValue[]
    passengerSeatType: OptionValue[]
  }
  /** Row configs: PDM labels + Dato images. See ROW_CONFIGURATION_PDM_DATO_MAPPING.md §6.1 */
  rowsData: {
    pdm: {
      /** Full PDM part_attributes per matched product (may duplicate attribute_value) */
      rawAttributes: {
        front: Array<{
          attribute: string
          attribute_value: string
          record_number?: number
          item_part_attribute_id?: number
          tags?: unknown[]
        }>
        back: Array<{
          attribute: string
          attribute_value: string
          record_number?: number
          item_part_attribute_id?: number
          tags?: unknown[]
        }>
        middle: Array<{
          attribute: string
          attribute_value: string
          record_number?: number
          item_part_attribute_id?: number
          tags?: unknown[]
        }>
      }
      /** Deduplicated attribute_value strings */
      uniqueValues: {
        front: string[]
        back: string[]
        middle: string[]
      }
      /** OptionValue[] used in the rows UI (with Dato images) */
      processed: {
        front: OptionValue[]
        back: OptionValue[]
        middle: OptionValue[]
      }
    }
    dato: {
      /** allPdmAssets from Dato */
      raw: PdmAssetRecord[] | null
      /** Trimmed to this vehicle's PDM attribute_values */
      filtered: PdmAssetRecord[] | null
    }
  } | null
  /**
   * Factory Match Dato asset record specifically extracted for easy access.
   *
   * SOURCE: Dato CMS "Factory Match" pdmAsset (fetched via fetchPdmAssetRowConfigurations())
   * CONTAINS: Array of `rows` with title/value/image for factory match options
   * SET IN: buildConfigurableStepsFromPdmProducts.ts (line ~1668)
   * USED IN: buildConfigurableStepsFromPdmProducts.ts aggregateFactoryMatches()
   *          to match Dato images against PDM-sourced factory match option titles/values
   */
  factoryMatchDatoAsset: PdmAssetRecord | null
  factoryMatches: FactoryMatch[]
  datoStepsBeforeMerge: Step[] | null
  mergedSteps: Step[] | null
  extractedAt: string | null
}

interface PdmDataState {
  pdmData: PdmExtractedData
  actions: {
    setPdmExtractedData: (data: Partial<PdmExtractedData>) => void
    resetPdmData: () => void
    logComparison: () => void
    exportDatoStructureForConstant: () => void
    logPdmOptionsOnly: () => void
    logRawPdmData: () => void
    logPdmAssetRows: () => void
    logPdmRows: () => void
  }
}

const initialPdmData: PdmExtractedData = {
  totalProducts: 0,
  matchedRows: 0,
  vehicleContext: null,
  rawPdmProducts: null,
  body: {
    trim: [],
    bodyType: [],
    driverSeatType: [],
    passengerSeatType: [],
  },
  rowsData: null,
  factoryMatchDatoAsset: null,
  factoryMatches: [],
  datoStepsBeforeMerge: null,
  mergedSteps: null,
  extractedAt: null,
}

export const usePdmDataStore = create<PdmDataState>()(
  devtools(
    (set, get) => ({
      pdmData: initialPdmData,

      actions: {
        setPdmExtractedData: (data) =>
          set((state) => ({
            pdmData: {
              ...state.pdmData,
              ...data,
              extractedAt: new Date().toISOString(),
            },
          })),

        resetPdmData: () =>
          set({
            pdmData: initialPdmData,
          }),

        logComparison: () => {
          const { pdmData } = get()

          console.group("📊 PDM vs DATO COMPARISON")

          console.log("🕐 Extracted at:", pdmData.extractedAt)
          console.log("🎯 Vehicle:", pdmData.vehicleContext)
          console.log("📦 Total PDM products:", pdmData.totalProducts)
          console.log("✅ Matched rows:", pdmData.matchedRows)
          console.log(
            "🔍 Raw PDM products available:",
            pdmData.rawPdmProducts ? "Yes" : "No"
          )
          console.log(
            "🪑 Rows data available:",
            pdmData.rowsData
              ? `Yes (PDM raw attrs: ${
                  pdmData.rowsData.pdm.rawAttributes.front.length +
                  pdmData.rowsData.pdm.rawAttributes.back.length +
                  pdmData.rowsData.pdm.rawAttributes.middle.length
                }, unique values: ${
                  pdmData.rowsData.pdm.uniqueValues.front.length +
                  pdmData.rowsData.pdm.uniqueValues.back.length +
                  pdmData.rowsData.pdm.uniqueValues.middle.length
                })`
              : "No"
          )

          console.groupCollapsed("🔧 BODY OPTIONS")
          console.table({
            Trim: {
              "PDM Count": pdmData.body.trim.length,
              "PDM Values": pdmData.body.trim
                .map((o) => o.optionName)
                .join(", "),
              "Dato Count":
                pdmData.datoStepsBeforeMerge
                  ?.find((s) => s.key === "body")
                  ?.substeps?.find((ss) => ss.key === "trim")?.category
                  ?.optionValues?.length || 0,
            },
            "Body Type": {
              "PDM Count": pdmData.body.bodyType.length,
              "PDM Values": pdmData.body.bodyType
                .map((o) => o.optionName)
                .join(", "),
              "Dato Count":
                pdmData.datoStepsBeforeMerge
                  ?.find((s) => s.key === "body")
                  ?.substeps?.find((ss) => ss.key === "body-step")?.category
                  ?.optionValues?.length || 0,
            },
            "Driver Seat": {
              "PDM Count": pdmData.body.driverSeatType.length,
              "PDM Values": pdmData.body.driverSeatType
                .map((o) => o.optionName)
                .join(", "),
              "Dato Count":
                pdmData.datoStepsBeforeMerge
                  ?.find((s) => s.key === "body")
                  ?.substeps?.find((ss) => ss.key === "driver-seat-type")
                  ?.category?.optionValues?.length || 0,
            },
            "Passenger Seat": {
              "PDM Count": pdmData.body.passengerSeatType.length,
              "PDM Values": pdmData.body.passengerSeatType
                .map((o) => o.optionName)
                .join(", "),
              "Dato Count":
                pdmData.datoStepsBeforeMerge
                  ?.find((s) => s.key === "body")
                  ?.substeps?.find((ss) => ss.key === "passenger-seat-type")
                  ?.category?.optionValues?.length || 0,
            },
          })
          console.groupEnd()

          console.groupCollapsed("🪑 ROWS DATA")

          if (!pdmData.rowsData) {
            console.log("No rows data available")
          } else {
            // Show raw PDM part_attributes with metadata
            console.log("📦 PDM Raw part_attributes (with metadata):")
            console.log(
              "Front Row attributes:",
              pdmData.rowsData.pdm.rawAttributes.front
            )
            console.log(
              "Back Row attributes:",
              pdmData.rowsData.pdm.rawAttributes.back
            )
            console.log(
              "Middle Row attributes:",
              pdmData.rowsData.pdm.rawAttributes.middle
            )

            // Show unique values (deduplicated)
            console.log("")
            console.log("🔹 PDM Unique attribute_values (deduplicated):")
            console.table({
              "Front Row": {
                Count: pdmData.rowsData.pdm.uniqueValues.front.length,
                Values:
                  pdmData.rowsData.pdm.uniqueValues.front.join(", ") ||
                  "(none)",
              },
              "Back Row": {
                Count: pdmData.rowsData.pdm.uniqueValues.back.length,
                Values:
                  pdmData.rowsData.pdm.uniqueValues.back.join(", ") || "(none)",
              },
              "Middle Row": {
                Count: pdmData.rowsData.pdm.uniqueValues.middle.length,
                Values:
                  pdmData.rowsData.pdm.uniqueValues.middle.join(", ") ||
                  "(none)",
              },
            })

            // Show processed OptionValue objects
            console.log("")
            console.log("🔄 PDM Processed (as OptionValue objects):")
            console.table({
              "Front Row": {
                "PDM Count": pdmData.rowsData.pdm.processed.front.length,
                "PDM Values": pdmData.rowsData.pdm.processed.front
                  .map((o) => o.optionName)
                  .join(", "),
                "Dato Count":
                  pdmData.datoStepsBeforeMerge
                    ?.find((s) => s.key === "rows")
                    ?.substeps?.find((ss) => ss.key === "front")?.category
                    ?.optionValues?.length || 0,
              },
              "Back Row": {
                "PDM Count": pdmData.rowsData.pdm.processed.back.length,
                "PDM Values": pdmData.rowsData.pdm.processed.back
                  .map((o) => o.optionName)
                  .join(", "),
                "Dato Count":
                  pdmData.datoStepsBeforeMerge
                    ?.find((s) => s.key === "rows")
                    ?.substeps?.find((ss) => ss.key === "back")?.category
                    ?.optionValues?.length || 0,
              },
              "Middle Row": {
                "PDM Count": pdmData.rowsData.pdm.processed.middle.length,
                "PDM Values": pdmData.rowsData.pdm.processed.middle
                  .map((o) => o.optionName)
                  .join(", "),
                "Dato Count":
                  pdmData.datoStepsBeforeMerge
                    ?.find((s) => s.key === "rows")
                    ?.substeps?.find((ss) => ss.key === "middle")?.category
                    ?.optionValues?.length || 0,
              },
            })

            // Show Dato asset counts
            console.log("🖼️ Dato PDM Assets:")
            console.log(
              `  Raw: ${
                pdmData.rowsData.dato.raw?.length || 0
              } assets (all configurations)`
            )
            console.log(
              `  Filtered: ${
                pdmData.rowsData.dato.filtered?.length || 0
              } assets (car-specific)`
            )
          }

          console.groupEnd()

          console.groupCollapsed("🏭 FACTORY MATCHES")
          console.log("PDM Factory Matches:", pdmData.factoryMatches.length)
          console.log(
            "Dato Factory Matches:",
            pdmData.datoStepsBeforeMerge?.find((s) => s.key === "factory-match")
              ?.factoryMatches?.length || 0
          )
          console.table(
            pdmData.factoryMatches.slice(0, 10).map((fm) => ({
              SKU: fm.sku,
              ID: fm.id,
            }))
          )
          console.groupEnd()

          console.groupCollapsed("📋 DATO STEPS (BEFORE MERGE)")
          console.log(pdmData.datoStepsBeforeMerge)
          console.groupEnd()

          console.groupCollapsed("🔄 MERGED STEPS (AFTER PDM OVERLAY)")
          console.log(pdmData.mergedSteps)
          console.groupEnd()

          console.groupEnd()
        },

        /**
         * Export Dato structure formatted for copying into base-step-structure.ts constant
         * Run this in console: usePdmDataStore.getState().actions.exportDatoStructureForConstant()
         */
        exportDatoStructureForConstant: () => {
          const { pdmData } = get()

          if (!pdmData.datoStepsBeforeMerge) {
            console.warn("⚠️ No Dato steps available. Select a vehicle first.")
            return
          }

          console.group("📋 EXPORT DATO STRUCTURE FOR CONSTANT")
          console.log("Copy the structure below into base-step-structure.ts:")
          console.log("")
          console.log("// Copy this JSON and format it as TypeScript:")
          console.log(JSON.stringify(pdmData.datoStepsBeforeMerge, null, 2))
          console.log("")
          console.log("Instructions:")
          console.log("1. Copy the JSON above")
          console.log(
            "2. For PDM-merged steps (body, rows, build, factory-match):"
          )
          console.log("   - Keep structure (id, key, title)")
          console.log("   - Set optionValues: []")
          console.log("3. For static steps (kit, pattern, design, material):")
          console.log("   - Keep full optionValues arrays")
          console.log("4. See constants/README.md for detailed instructions")
          console.groupEnd()
        },

        /**
         * Log ONLY the PDM-sourced options (body, rows, factory-match)
         * Shows what PDM provides vs what's in base-step-structure.ts
         * Run this in console: usePdmDataStore.getState().actions.logPdmOptionsOnly()
         */
        logPdmOptionsOnly: () => {
          const { pdmData } = get()

          if (!pdmData.vehicleContext) {
            console.warn(
              "⚠️ No PDM data available. Select a vehicle with PDM V2 enabled."
            )
            return
          }

          console.group("📦 PDM OPTION VALUES (from PDM products)")
          console.log("🎯 Vehicle:", pdmData.vehicleContext)
          console.log("📊 Total products:", pdmData.totalProducts)
          console.log("✅ Matched rows:", pdmData.matchedRows)
          console.log("")

          console.groupCollapsed("🔧 BODY SUBSTEP OPTIONS")
          console.table({
            Trim: {
              Count: pdmData.body.trim.length,
              Options: pdmData.body.trim.map((o) => o.optionName).join(", "),
            },
            "Body Type": {
              Count: pdmData.body.bodyType.length,
              Options: pdmData.body.bodyType
                .map((o) => o.optionName)
                .join(", "),
            },
            "Driver Seat": {
              Count: pdmData.body.driverSeatType.length,
              Options: pdmData.body.driverSeatType
                .map((o) => o.optionName)
                .join(", "),
            },
            "Passenger Seat": {
              Count: pdmData.body.passengerSeatType.length,
              Options: pdmData.body.passengerSeatType
                .map((o) => o.optionName)
                .join(", "),
            },
          })
          console.log("Raw body data:", pdmData.body)
          console.groupEnd()

          console.groupCollapsed("🪑 ROWS SUBSTEP OPTIONS")
          if (pdmData.rowsData?.pdm.processed) {
            console.table({
              "Front Row": {
                Count: pdmData.rowsData.pdm.processed.front.length,
                Options: pdmData.rowsData.pdm.processed.front
                  .map((o) => o.optionName)
                  .join(", "),
              },
              "Back Row": {
                Count: pdmData.rowsData.pdm.processed.back.length,
                Options: pdmData.rowsData.pdm.processed.back
                  .map((o) => o.optionName)
                  .join(", "),
              },
              "Middle Row": {
                Count: pdmData.rowsData.pdm.processed.middle.length,
                Options: pdmData.rowsData.pdm.processed.middle
                  .map((o) => o.optionName)
                  .join(", "),
              },
            })
            console.log("Raw rows data:", pdmData.rowsData)
          } else {
            console.log("No rows data available")
          }
          console.groupEnd()

          console.groupCollapsed("🏭 FACTORY MATCHES")
          console.log("Total factory matches:", pdmData.factoryMatches.length)
          if (pdmData.factoryMatches.length > 0) {
            console.table(
              pdmData.factoryMatches.slice(0, 10).map((fm) => ({
                SKU: fm.sku,
                ID: fm.id,
              }))
            )
          }
          console.log("Raw factory matches:", pdmData.factoryMatches)
          console.groupEnd()

          console.log("")
          console.log(
            "💡 TIP: These optionValues arrays are what get populated"
          )
          console.log("   into the empty arrays in base-step-structure.ts")
          console.log("   when PDM merging happens.")
          console.groupEnd()
        },

        /**
         * Log raw PDM response data for debugging
         * Run this in console: usePdmDataStore.getState().actions.logRawPdmData()
         */
        logRawPdmData: () => {
          const { pdmData } = get()

          if (!pdmData.rawPdmProducts) {
            console.warn(
              "⚠️ No raw PDM data available. Select a vehicle with PDM V2 enabled."
            )
            return
          }

          console.group("🔍 RAW PDM RESPONSE DATA")
          console.log("📦 Total products:", pdmData.rawPdmProducts.length)
          console.log("🎯 Vehicle context:", pdmData.vehicleContext)
          console.log("🕐 Extracted at:", pdmData.extractedAt)
          console.log("")

          if (pdmData.rawPdmProducts.length > 0) {
            console.groupCollapsed("📝 Sample Product (first product)")
            console.log(pdmData.rawPdmProducts[0])
            console.groupEnd()

            console.groupCollapsed("📋 All Products Array")
            console.log(pdmData.rawPdmProducts)
            console.groupEnd()

            console.groupCollapsed("🔑 Product Field Structure (first product)")
            const sampleProduct = pdmData.rawPdmProducts[0]
            console.log("Available fields:", Object.keys(sampleProduct))
            console.table({
              part_number: sampleProduct.part_number || "(not found)",
              base_part_number: sampleProduct.base_part_number || "(not found)",
              applications: Array.isArray(sampleProduct.applications)
                ? `Array (${sampleProduct.applications.length} items)`
                : "(not found)",
              part_attributes: Array.isArray(sampleProduct.part_attributes)
                ? `Array (${sampleProduct.part_attributes.length} items)`
                : "(not found)",
              digital_assets: Array.isArray(sampleProduct.digital_assets)
                ? `Array (${sampleProduct.digital_assets.length} items)`
                : "(not found)",
            })
            console.groupEnd()
          }

          console.log("")
          console.log(
            "💡 TIP: Use this to inspect the raw PDM API response structure"
          )
          console.log("   for debugging field mappings and data extraction.")
          console.groupEnd()
        },

        /**
         * Log Dato CMS PDM Asset row configurations with image details
         * Run this in console: usePdmDataStore.getState().actions.logPdmAssetRows()
         */
        logPdmAssetRows: () => {
          const { pdmData } = get()

          if (!pdmData.rowsData?.dato.raw && !pdmData.rowsData?.dato.filtered) {
            console.warn(
              "⚠️ No Dato PDM Asset rows available. They may not have been fetched yet."
            )
            return
          }

          console.group("🖼️ DATO CMS: PDM ASSET ROWS")
          console.log("🕐 Extracted at:", pdmData.extractedAt)
          console.log("")

          // Log raw data
          if (pdmData.rowsData?.dato.raw) {
            console.group(
              `📦 RAW DATA (${pdmData.rowsData.dato.raw.length} assets)`
            )
            pdmData.rowsData.dato.raw.forEach((asset) => {
              console.group(
                `${asset.title || "Unknown"} (${asset.rows?.length || 0} rows)`
              )
              if (asset.rows?.length) {
                console.table(
                  asset.rows.map((row) => ({
                    title: row.title,
                    value: row.value,
                    key: row.key,
                    hasImage: !!row.image?.responsiveImage?.src,
                  }))
                )
              } else {
                console.log("No rows configured")
              }
              console.groupEnd()
            })
            console.groupEnd()
          }

          // Log filtered data
          if (pdmData.rowsData?.dato.filtered) {
            console.group(
              `✅ FILTERED DATA (${pdmData.rowsData.dato.filtered.length} assets) - Car-specific configurations`
            )
            pdmData.rowsData.dato.filtered.forEach((asset) => {
              console.group(
                `${asset.title || "Unknown"} (${
                  asset.rows?.length || 0
                } rows matching PDM)`
              )
              if (asset.rows?.length) {
                console.table(
                  asset.rows.map((row) => ({
                    title: row.title,
                    value: row.value,
                    key: row.key,
                    hasImage: !!row.image?.responsiveImage?.src,
                    imageUrl: row.image?.responsiveImage?.src
                      ? row.image.responsiveImage.src.substring(0, 80) + "..."
                      : "(no image)",
                  }))
                )
              } else {
                console.log("No matching rows for this car")
              }
              console.groupEnd()
            })
            console.groupEnd()
          }

          console.log("")
          console.log(
            "💡 TIP: Filtered data shows only row configurations available for the selected vehicle"
          )
          console.groupEnd()
        },

        /**
         * Log raw PDM row attribute_values
         * Run this in console: usePdmDataStore.getState().actions.logPdmRows()
         */
        logPdmRows: () => {
          const { pdmData } = get()

          if (!pdmData.rowsData) {
            console.warn(
              "⚠️ No PDM row data available. Vehicle may not have been selected yet."
            )
            return
          }

          console.group("🎯 PDM ROW ATTRIBUTE_VALUES")
          console.log("🕐 Extracted at:", pdmData.extractedAt)
          console.log(
            "🚗 Vehicle:",
            `${pdmData.vehicleContext?.make} ${pdmData.vehicleContext?.model} ${pdmData.vehicleContext?.year}`
          )
          console.log("")

          console.log(
            "📦 Raw part_attributes extracted from PDM (with full metadata):"
          )
          console.log("Front Row:", pdmData.rowsData.pdm.rawAttributes.front)
          console.log("Back Row:", pdmData.rowsData.pdm.rawAttributes.back)
          console.log("Middle Row:", pdmData.rowsData.pdm.rawAttributes.middle)

          console.log("")
          console.log("🔹 Unique attribute_values (deduplicated):")
          console.table({
            "Front Row": {
              Count: pdmData.rowsData.pdm.uniqueValues.front.length,
              Values:
                pdmData.rowsData.pdm.uniqueValues.front.join(", ") || "(none)",
            },
            "Back Row": {
              Count: pdmData.rowsData.pdm.uniqueValues.back.length,
              Values:
                pdmData.rowsData.pdm.uniqueValues.back.join(", ") || "(none)",
            },
            "Middle Row": {
              Count: pdmData.rowsData.pdm.uniqueValues.middle.length,
              Values:
                pdmData.rowsData.pdm.uniqueValues.middle.join(", ") || "(none)",
            },
          })

          console.log("")
          console.log("🔄 As OptionValue objects:")
          console.table({
            "Front Row": {
              Count: pdmData.rowsData.pdm.processed.front.length,
              "Option Values": pdmData.rowsData.pdm.processed.front
                .map((o) => o.optionValue)
                .join(", "),
              "Option Names": pdmData.rowsData.pdm.processed.front
                .map((o) => o.optionName)
                .join(", "),
            },
            "Back Row": {
              Count: pdmData.rowsData.pdm.processed.back.length,
              "Option Values": pdmData.rowsData.pdm.processed.back
                .map((o) => o.optionValue)
                .join(", "),
              "Option Names": pdmData.rowsData.pdm.processed.back
                .map((o) => o.optionName)
                .join(", "),
            },
            "Middle Row": {
              Count: pdmData.rowsData.pdm.processed.middle.length,
              "Option Values": pdmData.rowsData.pdm.processed.middle
                .map((o) => o.optionValue)
                .join(", "),
              "Option Names": pdmData.rowsData.pdm.processed.middle
                .map((o) => o.optionName)
                .join(", "),
            },
          })

          console.log("")
          console.log(
            "💡 TIP: These values are used to filter Dato pdmAsset rows"
          )
          console.groupEnd()
        },
      },
    }),
    { name: "PdmDataStore" }
  )
)
